import { useAuthStore } from '@/auth/authStore';
import type { AdminUser } from '@/auth/authStore';
import type { FrameSpec } from '@shared/types';

const BASE = import.meta.env.VITE_SERVER_URL ?? 'http://localhost:8787';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let message = res.statusText;
    try {
      const j = (await res.json()) as { error?: string; message?: string };
      if (j.error) code = j.error;
      if (j.message) message = j.message;
    } catch {
      /* */
    }
    if (res.status === 401) {
      useAuthStore.getState().clear();
    }
    throw new ApiError(res.status, code, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ── Public types reflected from the server ──────────────────────────

export interface AuthEnvelope {
  token: string;
  user: AdminUser;
}

export interface AdminStats {
  users: number;
  admins: number;
  banned: number;
  totalMatches: number;
  matchesLast24h: number;
  topPlayers: Array<{ id: string; displayName: string; mmr: number; rank: string }>;
  settingsLastWarmedAt: number;
}

export interface CountryRow {
  country: string | null;
  count: number;
}

export interface AdminUserRow {
  id: string;
  email: string | null;
  username: string | null;
  displayName: string;
  role: 'USER' | 'MODERATOR' | 'ADMIN';
  mmr: number;
  rank: string;
  coins: number;
  gems: number;
  xp: number;
  level: number;
  isGuest: boolean;
  isBanned: boolean;
  bannedUntil: string | null;
  matchesPlayed: number;
  matchesWon: number;
  createdAt: string;
  lastSeenAt: string | null;
}

export interface SettingItem {
  key: string;
  type: 'number' | 'integer' | 'boolean' | 'string' | 'json';
  category: string;
  description: string;
  defaultValue: unknown;
  value: unknown;
  min?: number;
  max?: number;
}

export type CosmeticSlot = 'hat' | 'body' | 'cape' | 'victoryShout' | 'avatarFrame' | 'endgameFx' | 'matchBackground';
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface AdminCosmetic {
  id: string;
  slot: CosmeticSlot;
  name: string;
  description: string | null;
  rarity: Rarity;
  priceCoins: number;
  priceGems: number;
  iconUrl: string | null;
  data: Record<string, unknown>;
  isActive: boolean;
  isStarter: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminBundle {
  id: string;
  name: string;
  description: string | null;
  rarity: Rarity;
  priceCoins: number;
  priceGems: number;
  bannerUrl: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  character: { id: string; name: string; idleUrl: string | null };
  avatarFrame: { id: string; name: string; iconUrl: string | null };
  finisher: {
    id: string;
    name: string;
    iconUrl: string | null;
    data: Record<string, unknown>;
  };
}

export interface GameTip {
  id: string;
  text: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type ShopSaleItemType = 'cosmetic' | 'character' | 'holdSkin' | 'bundle' | 'clanEmblem' | 'skill';

export interface ShopSaleCatalogItem {
  itemType: ShopSaleItemType;
  subtype: string;
  id: string;
  name: string;
  priceCoins: number;
  priceGems: number;
  imageUrl: string | null;
}

export interface AdminShopSale {
  id: string;
  itemType: ShopSaleItemType;
  itemId: string;
  discountPercent: number;
  label: string | null;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminBoss {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  accentColor: string;
  isActive: boolean;
  sortOrder: number;
  scheduleDay: number | null;
  durationMs: number;
  normalHp: number;
  hardHp: number;
  gripMax: number;
  staggerMax: number;
  attackIntervalNormalMs: number;
  attackIntervalHardMs: number;
  attackDamageNormal: number;
  attackDamageHard: number;
  wrongTapDamage: number;
  rewardCoinsNormal: number;
  rewardCoinsHard: number;
  rewardGemsFirstClear: number;
  dailyRewardLimit: number;
  rewardFrameId: string | null;
  rewardFrame: { id: string; name: string; iconUrl: string | null } | null;
  _count: { runs: number };
}

export interface AdminBossDailyPointTier {
  from: number;
  to: number;
  points: number;
}

export interface AdminBossWeeklyRewardTier {
  from: number;
  to: number;
  coins: number;
  gems: number;
  frameId: string | null;
}

export interface AdminBossCompetitionConfig {
  enabled: boolean;
  dailyRunLimit: number;
  tzOffsetMinutes: number;
  weeklyFinalizeDay: number;
  weeklyFinalizeHour: number;
  dailyRankPoints: AdminBossDailyPointTier[];
  weeklyRewards: AdminBossWeeklyRewardTier[];
}

export interface AdminBossCompetitionStatus {
  dailyPlayers: number;
  weeklyPlayers: number;
  dailyDamage: number;
  scoreDate: string;
  dayEndsAt: string;
  weekStart: string;
  weekEndsAt: string;
  rankingOpen: boolean;
}

export interface AuditEntry {
  id: string;
  action: string;
  target: string | null;
  payload: unknown;
  createdAt: string;
  actor: { id: string; displayName: string; email: string | null };
}

export const api = {
  // Auth
  login: (input: { identifier: string; password: string }) =>
    req<AuthEnvelope>('POST', '/auth/login', input),
  me: () => req<{ user: AdminUser }>('GET', '/auth/me'),

  // Admin
  stats: () => req<AdminStats>('GET', '/admin/stats'),
  countries: () => req<{ items: CountryRow[] }>('GET', '/admin/countries'),
  users: (q: string, page: number, limit = 25) =>
    req<{ total: number; page: number; limit: number; items: AdminUserRow[] }>(
      'GET',
      `/admin/users?q=${encodeURIComponent(q)}&page=${page}&limit=${limit}`,
    ),
  patchUser: (id: string, patch: Partial<AdminUserRow> & { bannedReason?: string | null }) =>
    req<{ user: AdminUserRow }>('PATCH', `/admin/users/${id}`, patch),
  settings: () => req<{ items: SettingItem[] }>('GET', '/admin/settings'),
  setSetting: (key: string, value: unknown) =>
    req<{ ok: true }>('PUT', `/admin/settings/${encodeURIComponent(key)}`, { value }),
  cosmetics: () => req<{ items: AdminCosmetic[] }>('GET', '/admin/cosmetics'),
  createCosmetic: (input: Omit<AdminCosmetic, 'id' | 'createdAt' | 'updatedAt'>) =>
    req<{ cosmetic: AdminCosmetic }>('POST', '/admin/cosmetics', input),
  patchCosmetic: (id: string, patch: Partial<AdminCosmetic>) =>
    req<{ cosmetic: AdminCosmetic }>('PATCH', `/admin/cosmetics/${id}`, patch),
  deleteCosmetic: (id: string) => req<{ ok: true }>('DELETE', `/admin/cosmetics/${id}`),
  shopSales: () => req<{ catalog: ShopSaleCatalogItem[]; sales: AdminShopSale[] }>('GET', '/admin/shop-sales/'),
  saveShopSale: (
    itemType: ShopSaleItemType,
    itemId: string,
    input: Pick<AdminShopSale, 'discountPercent' | 'label' | 'startsAt' | 'endsAt' | 'isActive'>,
  ) => req<{ sale: AdminShopSale }>(
    'PUT',
    `/admin/shop-sales/${itemType}/${encodeURIComponent(itemId)}`,
    input,
  ),
  deleteShopSale: (itemType: ShopSaleItemType, itemId: string) =>
    req<{ ok: true }>('DELETE', `/admin/shop-sales/${itemType}/${encodeURIComponent(itemId)}`),
  bundles: () => req<{ items: AdminBundle[] }>('GET', '/admin/bundles'),
  patchBundle: (
    id: string,
    patch: Partial<Pick<
      AdminBundle,
      'name' | 'description' | 'rarity' | 'priceCoins' | 'priceGems' | 'bannerUrl' | 'isActive' | 'sortOrder'
    >>,
  ) => req<{ bundle: AdminBundle }>('PATCH', `/admin/bundles/${id}`, patch),

  // Rank-tier default frames (image/style overrides)
  rankFrames: () =>
    req<{
      tiers: string[];
      defaults: Record<string, FrameSpec>;
      overrides: Record<string, FrameSpec>;
    }>('GET', '/admin/rank-frames'),
  setRankFrame: (tier: string, patch: FrameSpec) =>
    req<{ overrides: Record<string, FrameSpec> }>('PUT', `/admin/rank-frames/${tier}`, patch),
  resetRankFrame: (tier: string) =>
    req<{ overrides: Record<string, FrameSpec> }>('DELETE', `/admin/rank-frames/${tier}`),

  // Match-screen gameplay tips
  tips: () => req<{ items: GameTip[] }>('GET', '/admin/tips'),
  createTip: (input: { text: string; isActive?: boolean; sortOrder?: number }) =>
    req<{ item: GameTip }>('POST', '/admin/tips', input),
  patchTip: (id: string, patch: Partial<Pick<GameTip, 'text' | 'isActive' | 'sortOrder'>>) =>
    req<{ item: GameTip }>('PATCH', `/admin/tips/${id}`, patch),
  deleteTip: (id: string) => req<{ ok: true }>('DELETE', `/admin/tips/${id}`),

  // Solo PVE boss catalog + tuning.
  bosses: () => req<{ items: AdminBoss[] }>('GET', '/admin/bosses'),
  createBoss: (
    input: Omit<AdminBoss, 'id' | 'rewardFrame' | '_count'>,
  ) => req<{ item: AdminBoss }>('POST', '/admin/bosses', input),
  patchBoss: (
    id: string,
    patch: Partial<Omit<AdminBoss, 'id' | 'rewardFrame' | '_count'>>,
  ) => req<{ item: AdminBoss }>('PATCH', `/admin/bosses/${id}`, patch),
  bossCompetition: () =>
    req<{
      config: AdminBossCompetitionConfig;
      status: AdminBossCompetitionStatus;
    }>('GET', '/admin/bosses/competition'),
  setBossCompetition: (config: AdminBossCompetitionConfig) =>
    req<{ config: AdminBossCompetitionConfig }>(
      'PUT',
      '/admin/bosses/competition',
      config,
    ),
  settleBossCompetition: () =>
    req<{ result: { dailyRows: number; weeklyRows: number } }>(
      'POST',
      '/admin/bosses/competition/settle',
    ),

  audit: () => req<{ items: AuditEntry[] }>('GET', '/admin/audit'),

  // Push notifications
  pushBroadcast: (input: {
    title: string;
    body: string;
    data?: Record<string, string>;
    locale?: string;
  }) => req<{ sent: number }>('POST', '/admin/push/broadcast', input),
  pushTest: (input: { title: string; body: string; data?: Record<string, string> }) =>
    req<{ sent: number }>('POST', '/admin/push/test', input),

  /** Upload an image for a cosmetic icon. Returns the public URL. */
  uploadCosmeticIcon: async (file: File): Promise<{ url: string; filename: string; size: number }> => {
    const token = useAuthStore.getState().token;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/admin/uploads/cosmetic`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: fd,
      credentials: 'include',
    });
    if (!res.ok) {
      let code = `HTTP_${res.status}`;
      let message = res.statusText;
      try {
        const j = (await res.json()) as { error?: string; message?: string };
        if (j.error) code = j.error;
        if (j.message) message = j.message;
      } catch {
        /* */
      }
      throw new ApiError(res.status, code, message);
    }
    return (await res.json()) as { url: string; filename: string; size: number };
  },

  /** Upload a finisher media clip (image/gif/mp4/webm). Returns the public URL. */
  uploadFinisherMedia: async (
    file: File,
  ): Promise<{ url: string; filename: string; mime: string; size: number }> => {
    const token = useAuthStore.getState().token;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/admin/uploads/media`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: fd,
      credentials: 'include',
    });
    if (!res.ok) {
      let code = `HTTP_${res.status}`;
      let message = res.statusText;
      try {
        const j = (await res.json()) as { error?: string; message?: string };
        if (j.error) code = j.error;
        if (j.message) message = j.message;
      } catch {
        /* */
      }
      throw new ApiError(res.status, code, message);
    }
    return (await res.json()) as { url: string; filename: string; mime: string; size: number };
  },
};

/** Build an absolute URL for an /uploads/... path. The server-issued URL is
 * relative so the SPA works behind any proxy. */
export function resolveAssetUrl(maybeRelative: string | null | undefined): string | null {
  if (!maybeRelative) return null;
  if (maybeRelative.startsWith('http://') || maybeRelative.startsWith('https://')) {
    return maybeRelative;
  }
  // Source-controlled game art is bundled into the admin image as well. Keep
  // it same-origin; the API only serves /uploads and returns 404 for /assets.
  if (maybeRelative.startsWith('/assets/')) return maybeRelative;
  return `${BASE}${maybeRelative}`;
}
